import React from 'react'
import { createPortal } from 'react-dom'
import { Loader2, Music2, Trash2 } from 'lucide-react'
import {
  getProcessingArtist,
  getProcessingProgress,
  getProcessingStage,
  getProcessingThumbnailUrl,
  getProcessingTitle,
} from '../utils/processingJob'

const ProcessingQueueItem = ({ job, index, handleRemoveFromProcessing }) => {
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false)
  const [thumbnailFailed, setThumbnailFailed] = React.useState(false)

  const songTitle = getProcessingTitle(job)
  const songArtist = getProcessingArtist(job)
  const thumbnailUrl = getProcessingThumbnailUrl(job)
  const progress = getProcessingProgress(job)
  const stage = getProcessingStage(job)
  const queued = job.status === 'queued' || job.status === 'pending'

  React.useEffect(() => {
    setThumbnailFailed(false)
  }, [thumbnailUrl])

  return (
    <article className="relative overflow-hidden rounded-2xl border border-white/15 bg-[color:rgba(16,27,45,0.84)] px-3 py-3 shadow-[0_10px_24px_rgba(2,8,18,0.35)]">
      <div className="flex min-h-[88px] items-center gap-3">
        <div className="relative flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/15 bg-white/5 text-[var(--room-muted)]">
          {thumbnailUrl && !thumbnailFailed ? (
            <img
              src={thumbnailUrl}
              alt=""
              className="h-full w-full object-cover"
              onError={() => setThumbnailFailed(true)}
            />
          ) : (
            <Music2 className="h-5 w-5" aria-hidden />
          )}
          <span className="absolute bottom-1 left-1 rounded-md bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold text-white">
            {index + 1}
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-1 flex min-w-0 items-center gap-2">
            <p className="min-w-0 flex-1 truncate text-sm font-semibold text-[var(--room-text)]">{songTitle}</p>
            <span className="shrink-0 rounded-full border border-[color:rgba(46,168,255,0.45)] bg-[color:rgba(46,168,255,0.18)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--room-primary)]">
              {queued ? 'Queued' : 'Processing'}
            </span>
          </div>
          <p className="truncate text-xs text-[var(--room-muted)]">{songArtist}</p>
          <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-[var(--room-muted)]">
            <span className="truncate">{stage}</span>
            <span className="shrink-0 tabular-nums">{progress}%</span>
          </div>
          <div
            className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/10"
            role="progressbar"
            aria-label={`${songTitle} processing progress`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
          >
            <div
              className="h-full rounded-full bg-[var(--room-primary)] transition-[width] duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-[var(--room-muted)]" aria-hidden />
          {queued ? (
            <button
              type="button"
              onClick={() => setShowConfirmDialog(true)}
              className="room-focus-ring flex h-11 w-11 items-center justify-center rounded-xl border border-[color:rgba(255,102,122,0.35)] bg-[color:rgba(255,102,122,0.14)] text-[var(--room-danger)] hover:bg-[color:rgba(255,102,122,0.24)]"
              aria-label={`Remove ${songTitle} from processing queue`}
              title="Remove from processing queue"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      {showConfirmDialog ? createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/65 px-4 backdrop-blur-sm">
          <div className="room-elevated w-full max-w-sm rounded-2xl p-5">
            <h3 className="text-lg font-semibold text-[var(--room-text)]">Cancel Processing</h3>
            <p className="mt-2 text-sm text-[var(--room-muted)]">
              Remove "{songTitle}" from processing queue?
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowConfirmDialog(false)}
                className="room-focus-ring h-10 rounded-xl border border-white/15 bg-white/5 px-3 text-sm font-medium text-[var(--room-muted)] hover:bg-white/10 hover:text-[var(--room-text)]"
              >
                Keep
              </button>
              <button
                type="button"
                onClick={() => {
                  handleRemoveFromProcessing(job.id)
                  setShowConfirmDialog(false)
                }}
                className="room-focus-ring h-10 rounded-xl border border-[color:rgba(255,102,122,0.4)] bg-[color:rgba(255,102,122,0.18)] px-3 text-sm font-semibold text-[var(--room-danger)] hover:bg-[color:rgba(255,102,122,0.28)]"
              >
                Remove
              </button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}
    </article>
  )
}

export default ProcessingQueueItem
